/// <amd-module name="Feed/Legacy/Actions/FeedSettings/FeedDetailsHandlers" />
import * as Utils_String from "VSS/Core/Util/String";

import { FeedSettingsComponents } from "Feed/Common/Constants/Constants";
import * as PackageResources from "Feed/Common/Resources";
import { ValidateFeedName } from "Feed/Common/Utils/Validator";
import { IValidationResult } from "Feed/Legacy/Actions/FeedSettings/ValidationHandler";
import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { FeedRetentionChanges } from "Feed/Legacy/Types/FeedRetentionChanges";

/**
 * The user modified the feed name field
 */
export class ChangeFeedNameHandler {
    public static handle(state: IFeedSettingsState, emit: () => void, name: string): IValidationResult {
        if (name === state.feed().name) {
            state.feedName = null;
        } else {
            state.feedName = name;
        }

        const errorMessage = ValidateFeedName(name) ? "" : PackageResources.FeedCreationDialogContent_FeedNameError;
        const validation: IValidationResult = {
            componentKey: FeedSettingsComponents.feedName,
            errorMessage
        };

        emit();
        return validation;
    }
}

/**
 * RetentionV2: The user modified one of the feed's retention policy fields. Only used when FF is turned on.
 */
export class ChangeFeedRetentionHandler {
    public static handle(
        state: IFeedSettingsState,
        emit: () => void,
        newSettings: FeedRetentionChanges
    ): IValidationResult[] {
        const createIndividualValidationResults = (errorMessage1: string, errorMessage2: string) => {
            let errorMessage = [errorMessage1, errorMessage2];
            let component = [
                FeedSettingsComponents.feedRetentionPolicyMaxVersions,
                FeedSettingsComponents.feedRetentionPolicyDaysToKeep
            ];

            return component.map(
                (componentKey, i) =>
                    ({
                        errorMessage: errorMessage[i],
                        componentKey
                    } as IValidationResult)
            );
        };

        Number.isInteger =
            Number.isInteger ||
            function(value) {
                return typeof value === "number" && isFinite(value) && Math.floor(value) === value;
            };

        if (state.retentionPolicySettings.retentionPolicyLoading === true) {
            return createIndividualValidationResults(
                PackageResources.Error_RetentionPoliciesNotLoaded,
                PackageResources.Error_RetentionPoliciesNotLoaded
            );
        }

        let maxVersions = null;
        let maxDaysToKeep = null;
        let errorMessageMaxCount: string;
        let errorMessageMaxDaysToKeep: string;
        let hasLetters = false;

        // If the checkbox is turned off, return a null policy.
        if (newSettings == null) {
            if (state.retentionPolicySettings.retentionPolicy == null) {
                state.retentionPolicySettings.retentionPolicyToApply = null;
            } else {
                state.retentionPolicySettings.retentionPolicyToApply = {
                    countLimit: maxVersions = null,
                    daysToKeepRecentlyDownloadedPackages: maxDaysToKeep = null,
                    ageLimitInDays: 0
                };
            }

            emit();
            return createIndividualValidationResults("", "");
        }

        // Check if both fields are empty. Allows for simultaneous checking.
        if (newSettings.maximumCount === "") {
            errorMessageMaxCount = ChangeFeedRetentionHandler.getMaxCountErrorMessage(0, state, newSettings);
        }
        if (newSettings.daysToKeep === "") {
            errorMessageMaxDaysToKeep = ChangeFeedRetentionHandler.getDaysToKeepErrorMessage(0, state, newSettings);
        }

        if (errorMessageMaxCount && errorMessageMaxDaysToKeep) {
            state.retentionPolicySettings.retentionPolicyToApply = {
                countLimit: 0,
                daysToKeepRecentlyDownloadedPackages: 0,
                ageLimitInDays: 0
            };

            return createIndividualValidationResults(errorMessageMaxCount, errorMessageMaxDaysToKeep);
        }

        maxVersions = Number(newSettings.maximumCount);
        errorMessageMaxCount = ChangeFeedRetentionHandler.getMaxCountErrorMessage(maxVersions, state, newSettings);

        maxDaysToKeep = Number(newSettings.daysToKeep);
        errorMessageMaxDaysToKeep = ChangeFeedRetentionHandler.getDaysToKeepErrorMessage(
            maxDaysToKeep,
            state,
            newSettings
        );

        if (
            newSettings.maximumCount != null &&
            newSettings.daysToKeep != null &&
            newSettings.maximumCount !== "" &&
            newSettings.daysToKeep !== ""
        ) {
            if (!Number.isInteger(Number(newSettings.maximumCount))) {
                errorMessageMaxCount = `${errorMessageMaxCount} ${ PackageResources.FeedDetailsPane_IntegersOnly }`;
                hasLetters = true;
            }
            if (!Number.isInteger(Number(newSettings.daysToKeep))) {
                errorMessageMaxDaysToKeep  = `${errorMessageMaxDaysToKeep} ${ PackageResources.FeedDetailsPane_IntegersOnly }`;
                hasLetters = true;
            }
        }

        // If the input is incorrect (not within bounds or has letters), return a different state and the errors.
        // If the input has letters, we revert back to the previous state and send an error with the letter included.
        if (errorMessageMaxCount || errorMessageMaxDaysToKeep) {
            if (hasLetters) {
                state.retentionPolicySettings.retentionPolicyToApply = {
                    countLimit:
                        state.retentionPolicySettings.retentionPolicyToApply != null
                            ? state.retentionPolicySettings.retentionPolicyToApply.countLimit
                            : state.retentionPolicySettings.retentionPolicy.countLimit,
                    daysToKeepRecentlyDownloadedPackages:
                        state.retentionPolicySettings.retentionPolicyToApply != null
                            ? state.retentionPolicySettings.retentionPolicyToApply.daysToKeepRecentlyDownloadedPackages
                            : state.retentionPolicySettings.retentionPolicy.daysToKeepRecentlyDownloadedPackages,
                    ageLimitInDays: 0
                };
            } else {
                state.retentionPolicySettings.retentionPolicyToApply = {
                    countLimit: maxVersions,
                    daysToKeepRecentlyDownloadedPackages: maxDaysToKeep,
                    ageLimitInDays: 0
                };
            }
            return createIndividualValidationResults(errorMessageMaxCount, errorMessageMaxDaysToKeep);
        }

        // Check if the new policy is same as old policy. If it isn't, then set the policy to apply.
        if (
            state.retentionPolicySettings.retentionPolicy != null &&
            maxVersions === state.retentionPolicySettings.retentionPolicy.countLimit &&
            maxDaysToKeep === state.retentionPolicySettings.retentionPolicy.daysToKeepRecentlyDownloadedPackages
        ) {
            state.retentionPolicySettings.retentionPolicyToApply = null;
        } else {
            state.retentionPolicySettings.retentionPolicyToApply = {
                countLimit: maxVersions === null ? null : Number(newSettings.maximumCount),
                daysToKeepRecentlyDownloadedPackages: maxDaysToKeep === null ? null : Number(newSettings.daysToKeep),
                ageLimitInDays: 0
            };
        }

        emit();
        return createIndividualValidationResults("", "");
    }

    private static getMaxCountErrorMessage(
        input: number,
        state: IFeedSettingsState,
        newSettings: FeedRetentionChanges
    ) {
        return ValidateRetentionPolicyInput(
            input,
            state.retentionPolicySettings.retentionPolicyMinCountLimit,
            state.retentionPolicySettings.retentionPolicyMaxCountLimit
        )
            ? ""
            : Utils_String.format(
                  PackageResources.Error_InvalidPackageRetentionVersionCountLimit,
                  newSettings.maximumCount,
                  state.retentionPolicySettings.retentionPolicyMinCountLimit,
                  state.retentionPolicySettings.retentionPolicyMaxCountLimit
              );
    }

    private static getDaysToKeepErrorMessage(
        input: number,
        state: IFeedSettingsState,
        newSettings: FeedRetentionChanges
    ) {
        return ValidateRetentionPolicyInput(
            input,
            state.retentionPolicySettings.retentionPolicyMinDaysToKeepLimit,
            state.retentionPolicySettings.retentionPolicyMaxDaysToKeepLimit
        )
            ? ""
            : Utils_String.format(
                  PackageResources.Error_InvalidPackageRetentionDaysToKeepLimit,
                  newSettings.daysToKeep,
                  state.retentionPolicySettings.retentionPolicyMinDaysToKeepLimit,
                  state.retentionPolicySettings.retentionPolicyMaxDaysToKeepLimit
              );
    }
}

/**
 * The user modified the feed description field
 */
export class ChangeFeedDescriptionHandler {
    public static handle(state: IFeedSettingsState, emit: () => void, description: string): void {
        if (description === state.feed().description) {
            state.feedDescription = null;
        } else {
            state.feedDescription = description || "";
        }
        emit();
    }
}

/**
 * The user modified the hideDeletedPackageVersions box state
 */
export class ChangeHideDeletedPackageVersionsHandler {
    public static handle(state: IFeedSettingsState, emit: () => void, hideDeletedPackageVersions: boolean): void {
        if (hideDeletedPackageVersions === state.feed().hideDeletedPackageVersions) {
            state.hideDeletedPackageVersions = null;
        } else {
            state.hideDeletedPackageVersions = hideDeletedPackageVersions;
        }
        emit();
    }
}

/**
 * The user modified the hideDeletedPackageVersions box state
 */
export class ChangeBadgesEnabledHandler {
    public static handle(state: IFeedSettingsState, emit: () => void, badgesEnabled: boolean): void {
        if (badgesEnabled === state.feed().badgesEnabled) {
            state.badgesEnabled = null;
        } else {
            state.badgesEnabled = badgesEnabled;
        }
        emit();
    }
}

export function ValidateRetentionPolicyInput(versions: number, minValue: number, maxValue: number): boolean {
    return !isNaN(versions) && versions <= maxValue && versions >= minValue && versions % 1 === 0;
}
